import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { toast } from "sonner";
import { Sparkle, X, ChartLineUp } from "@phosphor-icons/react";
const KIND_LABEL = {
  recommendation: "Recommendation",
  forecast: "Forecast",
  risk_alert: "Risk alert",
  observation: "Observation",
};

export default function Analytics() {
  const [insights, setInsights] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/analytics/insights"); setInsights(data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/analytics/insights/generate");
      toast.success(`Generated ${data.count} insights via Claude Sonnet 4.5`);
      setInsights(data.insights);
    } catch { toast.error("AI generation failed"); }
    setBusy(false);
  };

  const dismiss = async (id) => {
    try { await api.post(`/analytics/insights/${id}/dismiss`); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = filter ? insights.filter(i => i.kind === filter) : insights;

  return (
    <div data-testid="analytics-page" className="page-fade">
      <PageHeader eyebrow="AI · Claude Sonnet 4.5" title="AI insights" description="AI-generated observations, forecasts, risk alerts and recommendations — labelled with confidence, never presented as fact." tint="plum" icon={Sparkle} actions={
        <button className="btn-primary" onClick={generate} disabled={busy} data-testid="ai-generate-btn"><Sparkle size={14} weight="fill"/>{busy?"Generating…":"Generate insights"}</button>
      }/>
      <div className="flex gap-2 mb-8 flex-wrap">
        <button onClick={()=>setFilter("")} data-testid="ai-filter-all" className={`pill-toggle ${!filter?"is-active":""}`}>All</button>
        {Object.entries(KIND_LABEL).map(([k,l])=>(
          <button key={k} onClick={()=>setFilter(k)} data-testid={`ai-filter-${k}`} className={`pill-toggle ${filter===k?"is-active":""}`}>{l}</button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="card-soft"><EmptyState icon={ChartLineUp} title="No AI insights yet" description="Generate a fresh batch using the button above. Powered by Claude Sonnet 4.5 via Emergent Universal Key." /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 stagger">
          {filtered.map((i) => (
            <div key={i._id} className="card-elevated" data-testid={`analytics-card-${i.kind}`}>
              <div className="flex items-start justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="status-chip chip-brand">{KIND_LABEL[i.kind] || i.kind}</span>
                  <span className="label-eyebrow">{i.module}</span>
                </div>
                <button onClick={()=>dismiss(i._id)} data-testid={`dismiss-${i._id}`} className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1 rounded-md hover:bg-muted"><X size={14}/></button>
              </div>
              <div className="font-display text-[19px] font-semibold mt-4 leading-snug tracking-tight">{i.title}</div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{i.body}</p>
              {i.data && Object.keys(i.data).length > 0 && (
                <details className="mt-5"><summary className="label-eyebrow cursor-pointer select-none">Evidence</summary>
                  <pre className="text-xs font-mono bg-muted/50 rounded-lg p-3 mt-2 overflow-x-auto border border-border/60">{JSON.stringify(i.data, null, 2)}</pre>
                </details>
              )}
              <div className="mt-5 text-xs text-muted-foreground flex items-center justify-between border-t border-border/60 pt-3">
                <span className="tabular">Confidence {(i.confidence * 100).toFixed(0)}%</span>
                <span>{fmtDate(i.generated_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
